import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherX, FeatherArrowRight, FeatherLoader } from "@subframe/core";
import {
  ParsedExcelData,
  ExcelCell,
  ExcelSheet,
  parseReportForViewer,
} from "../../data/api/trialBalanceApi";
import {
  parseSheetReference,
  SheetReference,
} from "../utils/formulaParser";
import { SourceSheetModal, BreadcrumbItem } from "./SourceSheetModal";
import {
  cellStyleToCSS,
  formatWithNumberFormat,
  parseMergedCells,
  MergedCellInfo,
} from "../utils/excelStyleUtils";

interface ExcelViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: number;
  reportName: string;
  fromDate?: string; // Databook from date (YYYY-MM-DD)
  toDate?: string; // Databook to date (YYYY-MM-DD)
}

interface SelectedCell {
  sheet: string;
  row: number;
  col: string;
  value: string | number | null;
  formula: string | null;
}

interface SourceSheetContext {
  isOpen: boolean;
  sheet: string;
  cell: string;
  row: number;
  col: string;
  parentLeadPath: string; // e.g., "IS > Employee cost"
}

export const ExcelViewerModal: React.FC<ExcelViewerModalProps> = ({
  isOpen,
  onClose,
  reportId,
  reportName,
  fromDate,
  toDate,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [excelData, setExcelData] = useState<ParsedExcelData | null>(null);
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [sourceSheetModal, setSourceSheetModal] = useState<SourceSheetContext | null>(null);

  const gridRef = useRef<HTMLDivElement>(null);

  // Load Excel data when modal opens
  useEffect(() => {
    if (isOpen && reportId) {
      loadExcelData();
    }
  }, [isOpen, reportId]);

  const loadExcelData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await parseReportForViewer(reportId);
      setExcelData(response.data);
      if (response.data.sheetNames.length > 0) {
        setActiveSheet(response.data.sheetNames[0]);
      }
    } catch (err: any) {
      console.error("Failed to load Excel data:", err);
      setError(err.message || "Failed to load Excel data");
    } finally {
      setLoading(false);
    }
  };

  // Get the current sheet data
  const currentSheet: ExcelSheet | null = excelData?.sheets[activeSheet] || null;

  // Parse merged cells into lookup map
  const mergedCellsMap = useMemo((): Map<string, MergedCellInfo> => {
    return parseMergedCells(currentSheet?.mergedCells);
  }, [currentSheet?.mergedCells]);

  // Helper: Get value from column A for a specific row in the sheet
  const getColumnAValue = (sheet: ExcelSheet, rowNum: number): string => {
    const row = sheet.rows[rowNum - 1]; // rows are 0-indexed
    if (row) {
      const cellA = row.find((c) => c.col === "A");
      if (cellA && cellA.value != null) {
        return String(cellA.value).trim();
      }
    }
    return "";
  };

  // Handle cell click - if cell has sheet reference, go directly to source
  const handleCellClick = useCallback(
    (cell: ExcelCell) => {
      // Update selected cell
      setSelectedCell({
        sheet: activeSheet,
        row: cell.row,
        col: cell.col,
        value: cell.value,
        formula: cell.formula,
      });

      // If cell has a sheet reference, directly open source modal
      if (cell.formula && currentSheet) {
        const ref = parseSheetReference(cell.formula);
        if (ref) {
          // Build parent lead path: activeSheet > ColumnAValue
          const rowName = getColumnAValue(currentSheet, cell.row);
          const parentLeadPath = rowName ? `${activeSheet} > ${rowName}` : activeSheet;

          setSourceSheetModal({
            isOpen: true,
            sheet: ref.sheet,
            cell: ref.cell,
            row: ref.row,
            col: ref.col,
            parentLeadPath,
          });
        }
      }
    },
    [activeSheet, currentSheet]
  );

  // Check if selected cell has a sheet reference (for showing button as backup)
  const sheetRef: SheetReference | null = selectedCell?.formula
    ? parseSheetReference(selectedCell.formula)
    : null;

  // Handle "Go to source" button click (backup for formula bar)
  const handleGoToSource = () => {
    if (sheetRef && currentSheet && selectedCell) {
      // Build parent lead path
      const rowName = getColumnAValue(currentSheet, selectedCell.row);
      const parentLeadPath = rowName ? `${activeSheet} > ${rowName}` : activeSheet;

      setSourceSheetModal({
        isOpen: true,
        sheet: sheetRef.sheet,
        cell: sheetRef.cell,
        row: sheetRef.row,
        col: sheetRef.col,
        parentLeadPath,
      });
    }
  };

  // Close source sheet modal
  const handleCloseSourceModal = () => {
    setSourceSheetModal(null);
  };

  // Format cell value for display
  const formatCellValue = (cell: ExcelCell): string => {
    const value = cell.value;
    if (value === null || value === undefined || value === "") {
      return "";
    }
    // Use number format from style if available
    return formatWithNumberFormat(value, cell.style?.numberFormat);
  };

  // Check if a cell is selected
  const isCellSelected = (cell: ExcelCell): boolean => {
    return (
      selectedCell !== null &&
      selectedCell.row === cell.row &&
      selectedCell.col === cell.col &&
      selectedCell.sheet === activeSheet
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal Container - Full Screen */}
      <div className="flex flex-col w-full h-full bg-default-background">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-border bg-neutral-50">
          <div className="flex items-center gap-4">
            <h2 className="text-heading-2 font-heading-2 text-default-font">
              {reportName}
            </h2>
          </div>
          <IconButton
            icon={<FeatherX />}
            onClick={onClose}
            size="medium"
          />
        </div>

        {/* Formula Bar */}
        <div className="flex items-center gap-4 px-6 py-3 border-b border-neutral-border bg-white">
          <div className="flex items-center gap-2 min-w-[100px]">
            <span className="text-body-bold font-body-bold text-subtext-color">
              Cell:
            </span>
            <span className="text-body font-body text-default-font">
              {selectedCell ? `${selectedCell.col}${selectedCell.row}` : "-"}
            </span>
          </div>
          <div className="flex-1 flex items-center gap-2">
            <span className="text-body-bold font-body-bold text-subtext-color">
              Formula:
            </span>
            <code className="flex-1 px-3 py-1 bg-neutral-100 rounded text-body font-mono text-default-font overflow-x-auto">
              {selectedCell?.formula || selectedCell?.value || "-"}
            </code>
          </div>
          {sheetRef && (
            <Button
              variant="brand-primary"
              size="small"
              icon={<FeatherArrowRight />}
              onClick={handleGoToSource}
            >
              Go to source ({sheetRef.sheet})
            </Button>
          )}
        </div>

        {/* Sheet Tabs */}
        <div className="flex items-center gap-1 px-6 py-2 border-b border-neutral-border bg-neutral-50 overflow-x-auto">
          {excelData?.sheetNames.map((sheetName) => (
            <button
              key={sheetName}
              onClick={() => {
                setActiveSheet(sheetName);
                setSelectedCell(null);
              }}
              className={`px-4 py-2 text-body font-body rounded-t-md transition-colors whitespace-nowrap ${
                activeSheet === sheetName
                  ? "bg-white text-brand-600 border border-b-0 border-neutral-border font-bold"
                  : "text-subtext-color hover:bg-neutral-100"
              }`}
            >
              {sheetName}
            </button>
          ))}
        </div>

        {/* Spreadsheet Grid */}
        <div
          ref={gridRef}
          className="flex-1 overflow-auto bg-white"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-3 text-subtext-color">
                <FeatherLoader className="animate-spin" />
                <span>Loading spreadsheet...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-error-600">{error}</div>
            </div>
          ) : currentSheet ? (
            <table className="border-collapse min-w-full">
              {/* Column widths */}
              {currentSheet.columnWidths && (
                <colgroup>
                  {/* Row number column */}
                  <col style={{ width: "50px", minWidth: "50px" }} />
                  {currentSheet.rows[0]?.map((cell) => (
                    <col
                      key={cell.col}
                      style={{
                        width: currentSheet.columnWidths?.[cell.col]
                          ? `${currentSheet.columnWidths[cell.col]}px`
                          : "120px",
                        minWidth: "60px",
                      }}
                    />
                  ))}
                </colgroup>
              )}
              {/* Column headers - sticky at top */}
              <thead className="sticky top-0 z-20">
                <tr>
                  <th className="sticky left-0 z-30 px-2 py-1 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300">
                    #
                  </th>
                  {currentSheet.rows[0]?.map((cell) => (
                    <th
                      key={cell.col}
                      className="px-3 py-1 text-center text-caption font-caption text-subtext-color bg-neutral-200 border border-neutral-300"
                    >
                      {cell.col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentSheet.rows.map((row, rowIndex) => {
                  const rowNum = rowIndex + 1;
                  const rowHeight = currentSheet.rowHeights?.[rowNum];
                  return (
                    <tr
                      key={rowIndex}
                      style={rowHeight ? { height: `${rowHeight}px` } : undefined}
                    >
                      {/* Row number header */}
                      <td className="sticky left-0 z-10 px-2 py-1 text-center text-caption font-caption text-subtext-color bg-neutral-100 border border-neutral-200">
                        {rowNum}
                      </td>
                      {row.map((cell) => {
                        const cellAddress = `${cell.col}${cell.row}`;
                        const mergeInfo = mergedCellsMap.get(cellAddress);

                        // Skip hidden cells (covered by merge)
                        if (mergeInfo?.isHidden) {
                          return null;
                        }

                        // Get cell style
                        const cellStyle = cellStyleToCSS(cell.style);

                        return (
                          <td
                            key={cellAddress}
                            onClick={() => handleCellClick(cell)}
                            colSpan={mergeInfo?.colSpan || 1}
                            rowSpan={mergeInfo?.rowSpan || 1}
                            className={`px-3 py-1 text-body font-body border border-neutral-200 max-w-[300px] truncate cursor-pointer transition-colors ${
                              isCellSelected(cell)
                                ? "bg-brand-100 border-brand-500 border-2"
                                : cell.formula && !cell.style?.backgroundColor
                                ? "bg-blue-50 hover:bg-blue-100"
                                : "hover:bg-neutral-50"
                            }`}
                            style={cellStyle}
                            title={
                              cell.formula
                                ? `Click to go to source\nFormula: ${cell.formula}\nValue: ${cell.value}`
                                : String(cell.value || "")
                            }
                          >
                            {formatCellValue(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-subtext-color">No data available</div>
            </div>
          )}
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between px-6 py-2 border-t border-neutral-border bg-neutral-50">
          <span className="text-caption font-caption text-subtext-color">
            {currentSheet
              ? `${currentSheet.maxRow} rows × ${currentSheet.maxCol} columns`
              : ""}
          </span>
          <span className="text-caption font-caption text-subtext-color">
            Click on cells with blue background to navigate to their source sheet.
          </span>
        </div>
      </div>

      {/* Source Sheet Modal */}
      {sourceSheetModal && excelData && (
        <SourceSheetModal
          isOpen={sourceSheetModal.isOpen}
          onClose={handleCloseSourceModal}
          sheetName={sourceSheetModal.sheet}
          sheetData={excelData.sheets[sourceSheetModal.sheet]}
          targetCell={sourceSheetModal.cell}
          targetRow={sourceSheetModal.row}
          targetCol={sourceSheetModal.col}
          parentLeadPath={sourceSheetModal.parentLeadPath}
          fromDate={fromDate}
          toDate={toDate}
          allSheets={excelData.sheets}
          breadcrumbs={[{ sheet: activeSheet, cell: selectedCell ? `${selectedCell.col}${selectedCell.row}` : "-" }]}
        />
      )}
    </div>
  );
};
